import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type BatteryMakerRow } from "../lib/api.ts";

// §0.4 battery-maker catalog curation. Devices receive only status='canonical'
// rows (maker/model/Ah/afp/chemistry); datasheet_url is staff-side reference
// only — the link each row's chemistry+capacity was verified against.
const CHEMISTRIES = ["", "AGM", "GEL", "Flooded", "LiFePO4", "NiCd"];
const STATUSES = ["canonical", "pending", "rejected"];
// Rate a model's Ah is quoted at, in hours ("" = unknown) — the device's "Capacity rating" selector follows it.
const RATES = ["", 20, 10, 5, 1];

type Draft = Partial<BatteryMakerRow>;

export function StaffBatteryMakersPage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["staff", "battery-makers"],
    queryFn: api.staffListBatteryMakers,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["staff", "battery-makers"] });

  const upsert = useMutation({
    mutationFn: (body: Draft) => api.staffUpsertBatteryMaker(body),
    onSuccess: () => { invalidate(); setEditing(null); },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.staffDeleteBatteryMaker(id),
    onSuccess: invalidate,
  });

  // Top-of-list filters — client-side (the whole catalog is ~120 rows).
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fChem, setFChem] = useState("");
  const [fAfp, setFAfp] = useState(false);
  const matches = (r: BatteryMakerRow) => {
    if (fStatus && r.status !== fStatus) return false;
    if (fChem && (r.chemistry ?? "") !== fChem) return false;
    if (fAfp && !r.afp) return false;
    if (q) {
      const hay = `${r.maker} ${r.model ?? ""} ${r.afp ?? ""} ${r.context ?? ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  };

  // One row (or the "new" placeholder) in edit mode at a time.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const busy = upsert.isPending || del.isPending;
  const err = (upsert.error ?? del.error) as Error | null;

  const startEdit = (r?: BatteryMakerRow) => {
    setEditing(r?.id ?? "new");
    setDraft(r ? { ...r } : { status: "canonical", chemistry: "AGM" });
  };
  const save = () => {
    if (!draft.maker?.trim()) return;
    upsert.mutate({
      id: editing === "new" ? undefined : editing!,
      maker: draft.maker,
      model: draft.model ?? null,
      status: draft.status ?? "canonical",
      alias_of: draft.alias_of ?? null,
      context: draft.context ?? null,
      capacity_ah: draft.capacity_ah != null && `${draft.capacity_ah}` !== "" ? Number(draft.capacity_ah) : null,
      capacity_rate_h: draft.capacity_rate_h ? Number(draft.capacity_rate_h) : null,
      afp: draft.afp ?? null,
      chemistry: draft.chemistry ?? null,
      datasheet_url: draft.datasheet_url ?? null,
    });
  };

  const field = (k: keyof Draft, w: string, placeholder = "") => (
    <input
      style={{ width: w }}
      value={(draft[k] as string | number | null) ?? ""}
      placeholder={placeholder}
      onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
    />
  );

  const editorRow = (
    <tr style={{ background: "rgba(59,130,246,0.08)" }}>
      <td>{field("maker", "9rem")}</td>
      <td>{field("model", "8rem", "(maker row)")}</td>
      <td>
        <select value={draft.status ?? "canonical"}
                onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        {field("capacity_ah", "4rem")}{" "}
        <select value={draft.capacity_rate_h ?? ""} title="Rate the Ah is quoted at (the battery label's basis)"
                onChange={(e) => setDraft((d) => ({ ...d, capacity_rate_h: e.target.value ? Number(e.target.value) : null }))}>
          {RATES.map((h) => <option key={h} value={h}>{h ? `C${h}` : "—"}</option>)}
        </select>
      </td>
      <td>
        <select value={draft.chemistry ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, chemistry: e.target.value || null }))}>
          {CHEMISTRIES.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
        </select>
      </td>
      <td>{field("afp", "6rem", "afp-NNNN")}</td>
      <td>{field("datasheet_url", "12rem", "https://…")}</td>
      <td>{field("context", "12rem")}</td>
      <td style={{ whiteSpace: "nowrap" }}>
        <button disabled={busy || !draft.maker?.trim()} onClick={save}>Save</button>{" "}
        <button className="secondary" disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
      </td>
    </tr>
  );

  return (
    <>
      <h1>Battery catalog</h1>
      <p className="muted">
        Cloud-curated maker/model catalog. Devices receive <b>canonical</b> rows only
        (maker, model, rated Ah, ActivFire afp, chemistry); afp drives the AS1851
        "ActivFire-listed only" filter on the device. <b>Datasheet</b> is a staff-side
        reference — the source each row's chemistry/capacity was verified against —
        and is never sent to devices. Any change bumps the catalog version; the fleet
        re-pulls on its next refresh.
      </p>

      {err && <p className="err">{err.message}</p>}
      {list.isLoading && <p className="muted">Loading…</p>}
      {list.error && <p className="err">Couldn't load — {(list.error as Error).message}</p>}
      {list.data && (
        <>
          <p style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap", alignItems: "center" }}>
            <button disabled={busy || editing !== null} onClick={() => startEdit()}>
              Add entry
            </button>
            <input
              style={{ width: "14rem" }}
              placeholder="Search maker / model / afp / notes…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <select value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
              <option value="">any status</option>
              {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
            </select>
            <select value={fChem} onChange={(e) => setFChem(e.target.value)}>
              <option value="">any chemistry</option>
              {CHEMISTRIES.filter(Boolean).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem" }}>
              <input type="checkbox" checked={fAfp} onChange={(e) => setFAfp(e.target.checked)} />
              ActivFire-listed only
            </label>
            <span className="muted">
              {list.data.makers.filter(matches).length} / {list.data.makers.length}
            </span>
          </p>
          <table>
            <thead>
              <tr>
                <th>Maker</th>
                <th>Model</th>
                <th>Status</th>
                <th>Ah</th>
                <th>Chem</th>
                <th>ActivFire</th>
                <th>Datasheet</th>
                <th>Notes</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {editing === "new" && editorRow}
              {list.data.makers.filter(matches).length === 0 && (
                <tr><td colSpan={9} className="muted">No rows match the filters.</td></tr>
              )}
              {list.data.makers.filter(matches).map((r) =>
                editing === r.id ? (
                  <FragmentRow key={r.id}>{editorRow}</FragmentRow>
                ) : (
                  <tr key={r.id} style={r.status !== "canonical" ? { opacity: 0.55 } : undefined}>
                    <td>{r.model ? <span className="muted">{r.maker}</span> : <b>{r.maker}</b>}</td>
                    <td>{r.model ?? <span className="muted">(maker)</span>}</td>
                    <td>{r.status}</td>
                    <td>{r.capacity_ah ?? ""}{r.capacity_ah != null && r.capacity_rate_h ? <span className="muted"> @C{r.capacity_rate_h}</span> : ""}</td>
                    <td>{r.chemistry ?? ""}</td>
                    <td>{r.afp ?? ""}</td>
                    <td>
                      {r.datasheet_url ? (
                        <a href={r.datasheet_url} target="_blank" rel="noreferrer noopener">
                          link
                        </a>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td style={{ maxWidth: "18rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        title={r.context ?? undefined}>
                      {r.context ?? ""}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <button className="secondary" disabled={busy || editing !== null}
                              onClick={() => startEdit(r)}>Edit</button>{" "}
                      <button className="secondary" disabled={busy || editing !== null}
                              onClick={() => {
                                if (confirm(`Delete ${r.maker}${r.model ? ` ${r.model}` : ""}?`))
                                  del.mutate(r.id);
                              }}>Delete</button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

// Key-carrying wrapper so the editor row can replace a data row in the map.
function FragmentRow({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
